import React, { Component } from 'react';

import { Grid } from 'components/grid';
import { Tabs } from 'components/tabs';
import { translate } from 'components/translate';
import { PAGE_VIEW_EVENTS } from 'configs/routes/constants';
import lodashGet from 'lodash/get';
import { inject, PropTypes as MobxPropTypes } from 'mobx-react';
import { ContentBasePage } from 'modules/content';
import { hideFixedActionsBar } from 'modules/content/helper';
import PropTypes from 'prop-types';

import { ConfigCertificate } from './configCertificate';
import { ConfigCustomization } from './configCustomization';
import { ConfigData } from './configData';
import { ConfigHelp } from './configHelp';
import ConfigService from './ConfigService';
import ConfigSparkleManager from './configSparkle/ConfigSparkleManager';
import { ConfigStreamingManager } from './configStreaming';
import { CONFIG_TABS } from './constants';
import { ZendeskChat } from './zendeskChat';

import SparkleCampaignService from '../campaigns/sparkle/SparkleCampaign.service';
import EventService from '../membershipEvent/MembershipEventService';

import './ConfigManager.scss';

@inject('ConfigStore', 'MembershipStore', 'UiStore')
export class ConfigManager extends Component {
  static propTypes = {
    ConfigStore: MobxPropTypes.objectOrObservableObject,
    MembershipStore: MobxPropTypes.objectOrObservableObject,
    UiStore: MobxPropTypes.objectOrObservableObject,
    t: PropTypes.func,
    history: PropTypes.object,
    location: PropTypes.object
  };

  constructor(props) {
    super(props);

    this.state = {
      tabIndex: lodashGet(this.props, 'location.state.tabIndex', 0),
      highlightTabs: []
    };
  }

  async componentDidMount() {
    await EventService.init();
    this.setHighlightTab();
  }

  get showStreamingTab() {
    const { isStreamingFreeEnabled } = this.props.MembershipStore;

    return !isStreamingFreeEnabled;
  }

  get showSparkleTab() {
    return SparkleCampaignService.hasLocaleSupport();
  }

  get tabs() {
    const { t } = this.props;
    const { isOnlineEventTemplate } = this.props.MembershipStore;

    const tabs = [
      { name: t('config_manager.data') },
      { name: t('config_manager.customization'), track: 'Customization' },
      ...(!isOnlineEventTemplate
        ? [
            {
              name: t('config_manager.certificate'),
              track: 'Certificate'
            }
          ]
        : []),
      { name: t('config_manager.help'), track: 'Help' },
      ...(this.showStreamingTab
        ? [
            {
              name: t('config_manager.streaming'),
              track: 'Streaming',
              dataHubTrack: {
                group: 'productEdit',
                name: 'changeTab_configuration'
              }
            }
          ]
        : []),
      ...(this.showSparkleTab
        ? [
            {
              name: t('config_manager.mobile_app'),
              track: 'Sparkle'
            }
          ]
        : []),
      {
        name: t('config_manager.zendesk_chat'),
        track: 'Zendesk Chat',
        dataHubTrack: { group: 'zendesk', name: 'configure_zendesk' }
      }
    ];

    return tabs;
  }

  beforeChange = (tabIndex) => {
    this.normalizeHighlightTab(tabIndex);
    this.setState({ tabIndex });

    return true;
  };

  setHighlightTab = () => {
    this.setState({ highlightTabs: ConfigService.highlightTabs });
  };

  normalizeHighlightTab = (tabIndex) => {
    const { highlightTabs } = this.state;

    if (highlightTabs.includes(tabIndex)) {
      this.setState({
        highlightTabs: highlightTabs.filter(
          (highlightTabIndex) => highlightTabIndex !== tabIndex
        )
      });
      const { eventName } =
        ConfigService.tabHighlightConfig.find(
          (tabConfig) => tabConfig.tabIndex === tabIndex
        ) || {};

      if (eventName) {
        EventService.dismissEvent(eventName);
      }
    }
  };

  visibleFixedBarId(tabId) {
    return `config__fixedBar-${tabId ? tabId : ''}`;
  }

  componentDidUpdate() {
    const { tabIndex } = this.state;
    const id = this.visibleFixedBarId(tabIndex);

    hideFixedActionsBar(id);
  }

  renderDataTab = () => {
    return (
      <ConfigData
        fixedBarId={this.visibleFixedBarId(CONFIG_TABS.DATA)}
        tabIndex={this.state.tabIndex}
      />
    );
  };

  renderCustomTab = () => {
    return (
      <ConfigCustomization
        fixedBarId={this.visibleFixedBarId(CONFIG_TABS.CUSTOM)}
        tabIndex={this.state.tabIndex}
      />
    );
  };

  renderCertificateTab = () => {
    const { isOnlineEventTemplate } = this.props.MembershipStore;

    if (!isOnlineEventTemplate) {
      return <ConfigCertificate />;
    }

    return null;
  };

  renderHelpTab = () => {
    return <ConfigHelp fixedBarId={this.visibleFixedBarId(CONFIG_TABS.HELP)} />;
  };

  renderStreamingTab = () => {
    if (this.showStreamingTab) {
      return (
        <ConfigStreamingManager
          fixedBarId={this.visibleFixedBarId(CONFIG_TABS.STREAMING)}
          tabIndex={this.state.tabIndex}
          showDrmConfig={ConfigService.showDrmConfig}
          isTabActivated={this.state.tabIndex === CONFIG_TABS.STREAMING}
        />
      );
    }

    return null;
  };

  renderSparkleTab = () => {
    if (this.showSparkleTab) {
      return (
        <ConfigSparkleManager
          fixedBarId={this.visibleFixedBarId(CONFIG_TABS.SPARKLE)}
          tabIndex={this.state.tabIndex}
        />
      );
    }

    return null;
  };

  renderZendeskTab = () => {
    return (
      <ZendeskChat fixedBarId={this.visibleFixedBarId(CONFIG_TABS.ZENDESK)} />
    );
  };

  render() {
    const actived = this.state.tabIndex;

    return (
      <div className="config-manager-wrapper">
        <ContentBasePage className="content__grid _mb-0 _mb-sm-6">
          <ContentBasePage.Header
            hideBackHandler
            title="config_manager.title"
          />

          <Grid block noborder transparent nopadding>
            <Tabs
              track={PAGE_VIEW_EVENTS.CONFIG}
              tabs={this.tabs}
              actived={actived}
              beforeTabChange={this.beforeChange}
              tabHighlight={this.state.highlightTabs}
            >
              {[
                this.renderDataTab(),
                this.renderCustomTab(),
                this.renderCertificateTab(),
                this.renderHelpTab(),
                this.renderStreamingTab(),
                this.renderSparkleTab(),
                this.renderZendeskTab()
              ].filter((tab) => tab)}
            </Tabs>
          </Grid>
        </ContentBasePage>
      </div>
    );
  }
}

export default translate()(ConfigManager);
